/**
 * Shared mutation helpers for store modules.
 */
import type { DocumentNode } from 'graphql'
import { Maybe, Mutation } from 'types/types'
import type { ActionContext } from 'vuex'

import { useApollo } from '@/util/apolloClient'

const { apolloClient } = useApollo()

/** The part of a mutation payload that reports the outcome. */
interface MutationOutcome {
  ok?: Maybe<boolean>
  errors?: Maybe<string>
}

/** Mutations whose payload reports `ok` and `errors`. */
export type ReportingMutation = Extract<
  { [K in keyof Mutation]: NonNullable<Mutation[K]> extends MutationOutcome ? K : never }[keyof Mutation],
  keyof Mutation
>

/** The payload of mutation `K`: `ok`, `errors`, and its data fields. */
export type MutationPayload<K extends ReportingMutation> = NonNullable<Mutation[K]> & MutationOutcome

/** Send a mutation and return its payload. Rejects on a network or server error. */
export async function mutatePayload<K extends ReportingMutation>(
  mutation: DocumentNode,
  variables: Record<string, unknown>,
  dataKey: K,
): Promise<MutationPayload<K> | null | undefined> {
  const response = await apolloClient.mutate<Pick<Mutation, K>>({ mutation, variables })
  return response.data?.[dataKey] as MutationPayload<K> | null | undefined
}

/** Send a best-effort mutation (heartbeats, session teardown) and ignore its outcome, including failures. */
export function mutateSilently<K extends ReportingMutation>(
  mutation: DocumentNode,
  dataKey: K,
  variables: Record<string, unknown> = {},
): void {
  mutatePayload(mutation, variables, dataKey).catch(() => {
    // Not worth reporting: the user did not ask for this request and cannot act on its failure
  })
}

/** Shown when a mutation fails without a reason of its own. */
export const MUTATION_FAILED_MESSAGE = 'Something went wrong. Please try again.'

/**
 * Run a mutation the user asked for. Resolves to the payload on success. On failure it passes
 * the server's reason (or MUTATION_FAILED_MESSAGE) to showError and resolves to null.
 */
export async function runReportedMutation<K extends ReportingMutation>(
  mutation: DocumentNode,
  variables: Record<string, unknown>,
  dataKey: K,
  showError: (message: string) => void,
): Promise<MutationPayload<K> | null> {
  try {
    const payload = await mutatePayload(mutation, variables, dataKey)
    if (payload?.ok) {
      return payload
    }
    showError(payload?.errors || MUTATION_FAILED_MESSAGE)
  } catch (error) {
    console.error(`${dataKey} failed:`, error)
    showError(MUTATION_FAILED_MESSAGE)
  }
  return null
}

/** The part of a vuex action context that reports failures. */
type ErrorReportingContext = Pick<ActionContext<unknown, unknown>, 'dispatch'>

/** Show a failure to the user in the snackbar, from inside a store action. */
export function showStoreError(context: ErrorReportingContext, message: string): void {
  context.dispatch('snackbar/showError', message, { root: true })
}

/** Run a mutation from a store action; failures are shown in the snackbar. Resolves like runReportedMutation. */
export function runStoreMutation<K extends ReportingMutation>(
  context: ErrorReportingContext,
  mutation: DocumentNode,
  variables: Record<string, unknown>,
  dataKey: K,
): Promise<MutationPayload<K> | null> {
  return runReportedMutation(mutation, variables, dataKey, (message) => showStoreError(context, message))
}
